import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import s from "./OPOnIframe.module.scss";
import { useAuth } from "utils/useAuth/useAuth";
import { refreshTokenPromise } from "utils/api";
import usePageState from "utils/usePageState";

const config = require(`../../../config/${process.env.REACT_APP_ENV}/env.json`);

const OPOnIframe = ({ path, statePath }) => {
  const { refreshToken, user, token } = useAuth();
  const { pageState } = usePageState({ pagePath: statePath });

  const iframeRef = useRef(null);
  const [isInit, setIsInit] = useState(true);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isRefreshTokenPromiseResolved, setIsRefreshTokenPromiseResolved] =
    useState(!refreshTokenPromise);
  const isTokenExpired = user.exp < Date.now() / 1000;

  /**
   * Edge case handling
   * When user enters an iframe tab with an expired but refreshable token, the iframe will receive the expired token
   * Hence, make the iframe stay in loading state until the token finishes refreshing
   */
  useEffect(() => {
    const tokenPromiseHandler = async () => {
      if (isTokenExpired && !refreshTokenPromise) {
        //manually trigger the refresher if token is expired and not already refreshing
        refreshToken();
      }
      if (refreshTokenPromise) {
        await refreshTokenPromise;
      }
      setIsRefreshTokenPromiseResolved(true);
    };
    tokenPromiseHandler();
  }, []);
  const isAwaitingTokenRefresh =
    isTokenExpired || !isRefreshTokenPromiseResolved;

  const injectToken = useCallback(() => {
    const receiver = iframeRef.current.contentWindow;
    receiver.postMessage(
      {
        type: "session",
        payload: {
          env: "Online",
          session: { isoAgent: user, token },
          portalChanges: isInit,
          url: path,
        }
      },
      `${config.OP}/user/login`,
    );
  }, [user, token, path, isInit]);

  const injectPageState = useCallback(() => {
    const receiver = iframeRef.current.contentWindow;
    receiver.postMessage(
      {
        type: "pageState",
        payload: {
          state: pageState,
        }
      },
      `${config.OP}/user/login`,
    );
  }, [pageState]);

  useEffect(() => {
    if (isInit || !token) return;
    injectToken();
  }, [user, token, path]);

  useEffect(() => {
    if (isInit) return;
    injectPageState();
  }, [pageState]);

  const IframeOnLoadHandler = () => {
    injectToken();
    injectPageState();

    setTimeout(() => {
      setIsIframeLoaded(true);
      setIsInit(false);
    }, 1500);
  };

  return (
    <div className={s.outerContainer}>
      <div className={s.innerContainer}>
        {!isAwaitingTokenRefresh && (
          <iframe
            id={`operation-online-iframe-${path}`}
            src={config.OP + path}
            ref={iframeRef}
            onLoad={IframeOnLoadHandler}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "4px",
              display: isIframeLoaded ? "block" : "none",
            }}
          >
            <p>Your browser does not support iframes.</p>
          </iframe>
        )}
        {(isAwaitingTokenRefresh || !isIframeLoaded) && (
          <div className={s.spinning}>
            <div className={s.spinningIcon}>
              <CircularProgress />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OPOnIframe;
